// One thin wrapper per backend endpoint the shop uses. Payload shapes mirror what the CakePHP
// actions read; see PLAN.md and the controller docblocks for the response shapes.
import { get, post } from './client.js';

/** @typedef {import('$lib/types').Product} Product */
/** @typedef {import('$lib/types').Profile} Profile */

/** @typedef {{ profile: Profile, au_purchase_limit?: number, au_gst_enabled?: boolean }} ProfileResponse */

// ---------------------------------------------------------------- products

/**
 * @param {Record<string, unknown>} filter
 * @param {string} search
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ products: Product[], page: number, count: number }>}
 */
export const findProducts = (filter, search, signal) =>
	get('products/find.json', { filter, search }, { signal });

/**
 * @param {number | string} product_id
 * @param {number} [qty]
 * @returns {Promise<{ product: Product }>}
 */
export const getProduct = (product_id, qty) =>
	get('products/get.json', qty === undefined ? { product_id } : { product_id, qty });

/**
 * @param {Array<{ id: number, cartCount: number, supplier: unknown, product_type: number, your_price: unknown }>} cart
 * @param {string} promo_code
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ multi_product_discounts?: Record<string, any>, cart_items?: Record<string, any> } | null>}
 */
export const calculateAllCartDiscounts = (cart, promo_code, signal) =>
	post('products/calculate-all-cart-discounts.json', { cart, promo_code }, { signal });

// ---------------------------------------------------------------- master data

/** @returns {Promise<{ brands: Array<{ id: number, name: string, type: number }> }>} */
export const listBrands = () => get('suppliers/listBrands.json');

/** @returns {Promise<{ categories: Array<any> }>} */
export const getCategories = () => get('categories/get.json');

/** @returns {Promise<{ ingredients: Array<{ id: number, name: string }> }>} */
export const getSearchableIngredients = () => get('ingredients/getSearchable.json');

/**
 * @param {string} [country]
 * @returns {Promise<{ regions: Record<string, string[]> }>}
 */
export const getRegions = (country) =>
	get('profile/regions.json', country === undefined ? undefined : { country });

// ---------------------------------------------------------------- profile / account

/** @returns {Promise<ProfileResponse>} */
export const getProfile = () => get('profile/profile.json');

/**
 * @param {Partial<Profile>} profile
 * @returns {Promise<ProfileResponse>}
 */
export const updateProfile = (profile) => post('users/updateProfile.json', profile);

/** @param {number} product_id @param {string} category_name @returns {Promise<ProfileResponse>} */
export const addToFavourites = (product_id, category_name) =>
	post('users/addToFavourites.json', { product_id, category_name });

/** @param {number} cat_from @param {number} cat_to @returns {Promise<ProfileResponse>} */
export const reOrderFavourites = (cat_from, cat_to) =>
	post('users/reOrderFavourites.json', { cat_from, cat_to });

/** @param {number} category_id @returns {Promise<ProfileResponse>} */
export const removeFavouritesCat = (category_id) =>
	post('users/removeFavouritesCat.json', { category_id });

/** @param {number} category_id @param {string} category_name @returns {Promise<ProfileResponse>} */
export const updateFavouritesCat = (category_id, category_name) =>
	post('users/updateFavouritesCat.json', { category_id, category_name });

/** @param {number} favourite_id @returns {Promise<ProfileResponse>} */
export const removeFavourite = (favourite_id) =>
	post('users/removeFavourite.json', { favourite_id });

/**
 * The backend reads `qty` (the old client sometimes sent `count`, which was ignored).
 * @param {number} product_id @param {string} template_name @param {number} qty
 * @returns {Promise<ProfileResponse>}
 */
export const addToTemplate = (product_id, template_name, qty) =>
	post('users/addToTemplate.json', { product_id, template_name, qty });

/** @param {number} tmp_from @param {number} tmp_to @returns {Promise<ProfileResponse>} */
export const reOrderTemplates = (tmp_from, tmp_to) =>
	post('users/reOrderTemplates.json', { tmp_from, tmp_to });

/** @param {number} template_id @returns {Promise<ProfileResponse>} */
export const removeTemplate = (template_id) => post('users/removeTemplate.json', { template_id });

/** @param {number} template_id @param {string} template_name @returns {Promise<ProfileResponse>} */
export const updateTemplate = (template_id, template_name) =>
	post('users/updateTemplate.json', { template_id, template_name });

/** @param {number} template_product_id @returns {Promise<ProfileResponse>} */
export const removeTemplateProduct = (template_product_id) =>
	post('users/removeTemplateProduct.json', { template_product_id });

/**
 * @param {Record<string, unknown>} declaration
 * @returns {Promise<ProfileResponse>}
 */
export const submitGstDeclaration = (declaration) =>
	post('users/submitGstDeclaration.json', declaration);

/** @returns {Promise<ProfileResponse>} */
export const withdrawGstDeclaration = () => post('users/withdrawGstDeclaration.json', {});

/**
 * @param {string | number} postcode
 * @returns {Promise<{ is_rural_delivery: boolean }>}
 */
export const isRuralDelivery = (postcode) =>
	get('users/isRuralDelivery/' + encodeURIComponent(String(postcode)) + '.json');

/**
 * Practitioner's patients (scripts page). The keyword is sent as a plain JSON string; the old
 * client URL-encoded it first, which broke multi-word searches.
 * @param {string} keyword
 * @returns {Promise<{ patients: Array<{ id: number, name: string, email: string }> }>}
 */
export const listPatients = (keyword) => get('users/listPatients.json', keyword);

/** @returns {Promise<{ results: { access_token: string, client_id: string } }>} */
export const getNzPostToken = () => get('addressChecker/token.json');

// ---------------------------------------------------------------- orders

/**
 * @param {Record<string, unknown>} filter
 * @returns {Promise<{ orders: Array<any> }>}
 */
export const listOrders = (filter) => get('orders/list.json', { filter });

/**
 * @param {Record<string, unknown>} filter
 * @returns {Promise<{ orders: Array<any> }>}
 */
export const listBackorders = (filter) => get('orders/backorders.json', { filter });

/** @param {number} backorder_id @returns {Promise<any>} */
export const cancelBackorder = (backorder_id) =>
	post('orders/cancelBackorder.json', { backorder_id });

/**
 * @param {Record<string, unknown>} delivery_address
 * @param {number} order_total
 * @returns {Promise<{ delivery_address: Record<string, any> & { freight?: number, freight_error?: string } }>}
 */
export const calculateFreight = (delivery_address, order_total) =>
	get('orders/calculateFreight.json', { delivery_address, order_total });

/**
 * @param {{ cart: unknown[], delivery_address: unknown, alternate_address: number, promo_code: string, payment_method: string, patient_id: unknown, location_id: unknown }} order
 * @returns {Promise<{ data: { url: string, error?: string } }>}
 */
export const completeOrder = (order) => post('orders/complete.json', order);

/**
 * Previous delivery patients (checkout shipping page). Plain JSON string, see `listPatients`.
 * @param {string} keyword
 * @returns {Promise<{ patients: Array<any> }>}
 */
export const listOrderPatients = (keyword) => get('orders/listPatients.json', keyword);

// ---------------------------------------------------------------- scripts

/**
 * @param {Record<string, unknown>} filter
 * @returns {Promise<{ scripts: Array<any> }>}
 */
export const listScripts = (filter) => get('scripts/list.json', { filter });

/**
 * @param {{ script_lines: unknown[], notes: string, patient_name: string, patient_email: string, patient_id: number, send_email: boolean }} script
 * @returns {Promise<{ data: { uid?: string, error?: string } }>}
 */
export const createScript = (script) => post('scripts/create.json', script);

/** @param {string} uid @returns {Promise<{ data: { message?: string } }>} */
export const emailScript = (uid) => post('scripts/email.json', { uid });

/** @param {number} id @returns {Promise<{ data: { message?: string } }>} */
export const inactivateScript = (id) => post('scripts/inactive.json', { id });
